import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../config/api'

interface User {
  id: string
  username: string
  email: string
  firstName: string
  lastName: string
  roles: string[]
  isActive: boolean
  canManageLogbook?: boolean
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  loading: boolean
  isAuthenticated: boolean
  databaseConnectionError: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null) // Store in memory only
  const [loading, setLoading] = useState(true)
  const [databaseConnectionError, setDatabaseConnectionError] = useState(false)

  // Configure axios to send cookies with requests
  axios.defaults.withCredentials = true

  // Attempt to restore session on app load via refresh token
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {}, {
          withCredentials: true
        })

        const { accessToken } = response.data.data
        setToken(accessToken)
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
        await fetchUser()
      } catch (error) {
        // No valid refresh token, user needs to login
        setLoading(false)
      }
    }

    restoreSession()
  }, [])

  // Update authorization header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete axios.defaults.headers.common['Authorization']
    }
  }, [token])

  // Auto-refresh token every 14 minutes (before 15min expiration)
  useEffect(() => {
    if (!token) return

    const refreshInterval = setInterval(async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {}, {
          withCredentials: true
        })
        const { accessToken } = response.data.data
        setToken(accessToken)
      } catch (error) {
        // Refresh failed, logout user
        logout()
      }
    }, 14 * 60 * 1000) // 14 minutes

    return () => clearInterval(refreshInterval)
  }, [token])

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/profile`, {
        withCredentials: true
      })
      const userData = response.data.data?.user || response.data.user
      setUser(userData)
      setDatabaseConnectionError(false)
    } catch (error: any) {
      // Check for database connection error
      if (error.response?.data?.error?.code === 'DATABASE_CONNECTION_ERROR') {
        setDatabaseConnectionError(true)
      }

      logout()
    } finally {
      setLoading(false)
    }
  }

  const login = async (username: string, password: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        username,
        password
      }, {
        withCredentials: true
      })

      const { data } = response.data
      const { accessToken: newToken, user: userData } = data

      setToken(newToken)
      setUser(userData)
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
      setDatabaseConnectionError(false)
    } catch (error: any) {
      // Check for database connection error
      if (error.response?.data?.error?.code === 'DATABASE_CONNECTION_ERROR') {
        setDatabaseConnectionError(true)
        throw new Error('Database connection failed. Please ensure the PostgreSQL database is running and try again.')
      }

      throw new Error(error.response?.data?.error?.message || error.response?.data?.message || 'Login failed')
    }
  }

  const logout = async () => {
    try {
      // Call backend logout to clear httpOnly refresh token cookie
      await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, {
        withCredentials: true
      })
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      setUser(null)
      setToken(null)
      delete axios.defaults.headers.common['Authorization']
    }
  }

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    databaseConnectionError
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}